/*
 * `KB.md` — the conventions both the owner and the agent edit.
 *
 * The agent is allowed to write here, which is the point: nobody wants to hand-write
 * every convention up front, and the rules have to change as the material shows what
 * works for this base. Safety comes from the same place it comes from in the wiki —
 * every write appends a revision, so a rule the agent degraded three edits ago is
 * recoverable rather than merely regrettable.
 *
 * Two details that are the API's and are surfaced rather than hidden. Reverting
 * writes a NEW revision (history is never rewritten), so the button says so. And the
 * locked preamble is not part of what is stored — it is prepended by the platform —
 * so it is rendered as read-only above the editable half and is never posted back as
 * if the editor owned it.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  listSchemaRevisions,
  writeSchema,
  type SchemaRevision,
  type SchemaRevisionList,
} from '../../../api/knowledgeBases.ts'
import { Empty, Loaded } from '../components/AsyncState.tsx'
import { DiffView } from '../components/DiffView.tsx'
import { dateTime, num } from '../lib/format.ts'
import { authorBadge, authorLabel } from '../lib/operations.ts'
import { countChanges, diffLines, toHunks } from '../lib/textDiff.ts'
import { useResource } from '../lib/useResource.ts'

interface SchemaTabProps {
  kbId: string
  onChanged: () => void
}

export function SchemaTab({ kbId, onChanged }: SchemaTabProps) {
  const revisions = useResource<SchemaRevisionList>(`${kbId}:schema`, () =>
    listSchemaRevisions(kbId),
  )
  const [selected, setSelected] = useState<number | null>(null)
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const items = revisions.data?.items ?? []
  const current = items[0] ?? null
  const active = useMemo(
    () => items.find((item) => item.revision === selected) ?? current,
    [items, selected, current],
  )
  const previous = useMemo(() => {
    if (!active) return null
    const index = items.findIndex((item) => item.revision === active.revision)
    return index >= 0 ? (items[index + 1] ?? null) : null
  }, [items, active])

  useEffect(() => {
    setEditing(false)
    setSelected(null)
    setError(null)
  }, [kbId])

  const lines = useMemo(
    () => (previous && active ? diffLines(previous.body_md, active.body_md) : []),
    [previous, active],
  )
  const hunks = useMemo(() => toHunks(lines), [lines])
  const counts = countChanges(lines)

  async function save(body: string, note: string) {
    setSaving(true)
    setError(null)
    try {
      await writeSchema(kbId, body)
      setEditing(false)
      setSelected(null)
      revisions.reload()
      onChanged()
    } catch (err) {
      setError(`${note}: ${err instanceof Error ? err.message : 'ошибка'}`)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="pane">
      <div className="callout callout--info" style={{ marginBottom: 16 }}>
        <b>
          <code>KB.md</code> правят и агент, и вы.
        </b>{' '}
        Правила должны меняться по мере того, как выясняется, что для этой темы работает, поэтому
        запись агента разрешена — но каждая запись создаёт ревизию, показывается диффом и
        откатывается в один клик. Откат тоже создаёт ревизию: история не перезаписывается.
      </div>

      {error ? (
        <div className="callout callout--danger" style={{ marginBottom: 14 }}>
          {error}
        </div>
      ) : null}

      <Loaded resource={revisions} what="ревизии схемы" skeletonRows={6}>
        {(data) => (
          <div className="schema">
            <div className="card">
              <div className="card__head">
                <h3>
                  KB.md{' '}
                  <span className="path">
                    ревизия r{active?.revision ?? 1}
                    {active && current && active.revision !== current.revision
                      ? ` (текущая r${current.revision})`
                      : ''}
                  </span>
                </h3>
                <div className="right">
                  {active ? (
                    <span className="pill pill--info pill--sm">
                      {authorBadge(active.author)} {authorLabel(active.author)}
                    </span>
                  ) : null}
                  {data.churn?.warn ? (
                    <span
                      className="pill pill--warn pill--sm"
                      title={`Порог: ${data.churn.threshold} правок в сутки`}
                    >
                      схему переписывают часто: {data.churn.revisions} за сутки
                    </span>
                  ) : null}
                  {editing ? (
                    <>
                      <button
                        type="button"
                        className="btn btn--primary btn--sm"
                        disabled={saving}
                        onClick={() => void save(draft, 'не удалось сохранить')}
                      >
                        {saving ? 'Сохраняем…' : 'Сохранить ревизию'}
                      </button>
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        disabled={saving}
                        onClick={() => setEditing(false)}
                      >
                        Отмена
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      disabled={!current}
                      onClick={() => {
                        setDraft(current?.body_md ?? '')
                        setSelected(null)
                        setEditing(true)
                      }}
                    >
                      Редактировать
                    </button>
                  )}
                </div>
              </div>

              {data.locked_preamble ? (
                <div style={{ padding: '13px 15px 0' }}>
                  <div className="editor__locked" title="Управляется админом, агент не меняет">
                    {data.locked_preamble}
                  </div>
                </div>
              ) : null}

              {editing ? (
                <>
                  <textarea
                    className="editor"
                    value={draft}
                    maxLength={200_000}
                    onChange={(event) => setDraft(event.target.value)}
                  />
                  <p className="field__hint" style={{ padding: '9px 15px 13px', margin: 0 }}>
                    Сохраняется только редактируемая половина файла — закреплённая преамбула выше
                    добавляется платформой и не хранится в ревизии.
                  </p>
                </>
              ) : active ? (
                <pre className="editor">{active.body_md}</pre>
              ) : (
                <Empty title="Ревизий пока нет" />
              )}
            </div>

            <div className="card">
              <div className="card__head">
                <h3>История ревизий</h3>
                <div className="right">
                  {/* История подрезается сборщиком мусора, поэтому общего числа
                      ревизий сервер не знает и не отдаёт: считаем показанные и,
                      если страница не последняя, говорим об этом прямо. */}
                  <span className="pill pill--neutral pill--sm">
                    {num(data.items.length)} ревизий{data.has_more ? ' (показаны последние)' : ''}
                  </span>
                </div>
              </div>

              {data.items.length === 0 ? (
                <Empty title="Ревизий пока нет">
                  Первая появится при создании базы из пресета.
                </Empty>
              ) : (
                <div>
                  {data.items.map((revision) => (
                    <RevisionRow
                      key={revision.revision}
                      revision={revision}
                      selected={active?.revision === revision.revision}
                      onSelect={() => {
                        setSelected(revision.revision)
                        setEditing(false)
                      }}
                    />
                  ))}
                </div>
              )}

              <hr className="hair" />

              <div className="card__body">
                <span className="kicker" style={{ display: 'block', marginBottom: 9 }}>
                  {previous && active
                    ? `Дифф r${previous.revision} → r${active.revision} · +${counts.added} −${counts.removed}`
                    : 'Дифф'}
                </span>
                {previous && active ? (
                  <DiffView hunks={hunks} mode="unified" />
                ) : (
                  <p className="field__hint">
                    {active
                      ? 'Это первая ревизия — сравнивать не с чем.'
                      : 'Выберите ревизию, чтобы увидеть, что изменилось.'}
                  </p>
                )}

                {previous && active ? (
                  <div className="filters" style={{ marginTop: 12 }}>
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      disabled={saving}
                      onClick={() =>
                        void save(previous.body_md, `не удалось откатить к r${previous.revision}`)
                      }
                    >
                      {saving ? 'Откатываем…' : `Откатить к r${previous.revision}`}
                    </button>
                    <span className="field__hint" style={{ margin: 0 }}>
                      Откат записывает новую ревизию с текстом r{previous.revision}, а не удаляет r
                      {active.revision}.
                    </span>
                  </div>
                ) : null}

                <p className="field__hint" style={{ marginTop: 11 }}>
                  Дифф между ревизиями считается в браузере: API отдаёт полный текст каждой ревизии
                  и не отдаёт разницу между ними.
                </p>
              </div>
            </div>
          </div>
        )}
      </Loaded>
    </div>
  )
}

function RevisionRow({
  revision,
  selected,
  onSelect,
}: {
  revision: SchemaRevision
  selected: boolean
  onSelect: () => void
}) {
  const agent = revision.author === 'agent' || revision.author === 'assistant'
  return (
    <button type="button" className="rev" aria-current={selected} onClick={onSelect}>
      <span className={`rev__who ${agent ? 'rev__who--agent' : 'rev__who--user'}`}>
        {authorBadge(revision.author)}
      </span>
      <span className="rev__main">
        <span className="rev__title">r{revision.revision}</span>
        <span className="rev__sub">
          {authorLabel(revision.author)} · {dateTime(revision.created_at)}
          {revision.run_id ? ` · run ${revision.run_id.slice(0, 7)}` : ''}
        </span>
      </span>
      <span className="rev__n">{num(revision.body_md.split('\n').length)} стр.</span>
    </button>
  )
}
